import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaPlay, FaBookmark, FaHistory, FaPaperclip, FaRegStickyNote, FaArrowRight, FaCheck } from "react-icons/fa";
import { Reveal } from "../../../components/common";
import samuraiHero from "../../../assets/course/samurai-hero.jpg";
import { useAuth } from "../../../context/AuthContext";
import { fetchCourseAccess } from "../../../services/learnService";

const FEATURES = [
  { icon: FaPlay, title: "Every class, on demand", desc: "Rewatch any Creative Samurai class right here on arvixcreative.com." },
  { icon: FaHistory, title: "Pick up where you left off", desc: "Your spot in each video and your completed classes are saved." },
  { icon: FaRegStickyNote, title: "Notes pinned to the timeline", desc: "Drop a note or bookmark at the exact second a technique clicks." },
  { icon: FaPaperclip, title: "Practice files per class", desc: "Download the AI/PSD files, brushes and briefs the class uses." },
];

const LESSONS = [
  { t: "Pen tool fundamentals", d: "done" },
  { t: "Pathfinder & Shape Builder", d: "active" },
  { t: "Strokes, fills & gradients", d: "" },
  { t: "Vector tracing & icon design", d: "" },
];

/** Home-page announcement: Arvix Academy now has recorded classes. */
const AcademyRecordedSection = () => {
  const { token, isStudent } = useAuth();
  const [canWatch, setCanWatch] = useState(false);

  useEffect(() => {
    if (!token) {
      setCanWatch(false);
      return undefined;
    }
    let cancelled = false;
    fetchCourseAccess("creative-samurai", { Authorization: `Bearer ${token}` })
      .then((a) => !cancelled && setCanWatch(Boolean(a.can_watch)))
      .catch(() => !cancelled && setCanWatch(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const primary = canWatch
    ? { to: "/learn/creative-samurai", label: isStudent ? "Continue watching" : "Open the classes" }
    : { to: "/courses/creative-samurai", label: "Explore Creative Samurai" };

  return (
    <section className="py-16 md:py-24">
      <div className="w-full">
        <div className="grid xl:grid-cols-[1.1fr_1fr] gap-10 xl:gap-14 items-center">
          {/* Copy */}
          <div className="min-w-0">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="tick" aria-hidden="true" />
                <span className="font-mono text-xs tracking-[0.25em] text-gray-400">01</span>
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-designer-primary">
                  Arvix Academy
                </p>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-designer-primary text-white text-[10px] font-bold tracking-[0.18em] uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  New
                </span>
              </div>
            </Reveal>
            <Reveal delay={80}>
            <h2 id="academy-recorded-title" className="text-balance text-3xl md:text-4xl xl:text-5xl font-display font-extrabold tracking-tight text-designer-dark leading-[1.05]">
              Recorded classes are <span className="text-designer-primary">live</span>
            </h2>
            </Reveal>
            <p className="mt-4 text-base md:text-lg text-gray-600 max-w-xl leading-relaxed">
              Enrolled Creative Samurai students can now watch every class directly on our site: rewind the tricky
              pen-tool moves, revise before a project, and learn at your own pace.
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-4">
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 w-9 h-9 shrink-0 rounded-xl bg-red-50 border border-red-100 text-designer-primary flex items-center justify-center">
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-designer-dark text-sm">{title}</span>
                    <span className="block text-gray-500 text-sm leading-relaxed">{desc}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3">
              <Link
                to={primary.to}
                className="group inline-flex w-full lg:w-auto items-center justify-center gap-2 px-6 py-3.5 xl:px-5 xl:py-3 xl:text-sm xl:whitespace-nowrap bg-designer-primary text-white font-semibold rounded-full hover:bg-red-700 transition-colors"
              >
                {canWatch ? <FaPlay className="w-3.5 h-3.5 shrink-0" /> : null}
                <span className="min-w-0">{primary.label}</span>
                <FaArrowRight className="w-3.5 h-3.5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Link>
              {!token && (
                <Link
                  to="/activate"
                  state={{ from: "/learn/creative-samurai" }}
                  className="inline-flex w-full lg:w-auto items-center justify-center gap-2 px-5 py-3.5 xl:px-5 xl:py-3 xl:text-sm xl:whitespace-nowrap rounded-full border border-designer-dark/20 text-designer-dark font-semibold hover:border-designer-primary hover:text-designer-primary transition-colors text-center"
                >
                  Already enrolled? Activate access
                </Link>
              )}
            </div>
          </div>

          {/* Player preview */}
          <Link
            to={primary.to}
            className="group relative block min-w-0 rounded-3xl bg-[#080808] p-3 md:p-4 shadow-2xl shadow-red-900/20 ring-1 ring-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary"
            aria-label={primary.label}
          >
            <div className="flex items-center gap-1.5 px-1 pb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="ml-3 min-w-0 flex-1 text-[10px] font-mono tracking-[0.22em] text-white/40 uppercase truncate">arvixcreative.com/learn/creative-samurai</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_1fr] gap-3">
              <div className="min-w-0">
                <div className="relative aspect-video rounded-xl overflow-hidden">
                  <img src={samuraiHero} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <span className="absolute inset-0 m-auto w-12 h-12 md:w-14 md:h-14 rounded-full bg-designer-primary text-white flex items-center justify-center shadow-lg shadow-red-500/50 transition-transform group-hover:scale-110">
                    <FaPlay className="w-4 h-4 ml-0.5" />
                  </span>
                  <span className="absolute left-3 right-3 bottom-2.5 text-white text-xs md:text-sm font-semibold leading-snug">Week 2 · Pathfinder &amp; Shape Builder</span>
                </div>
                <div className="relative mt-3 h-1.5 rounded-full bg-white/10">
                  <span className="absolute inset-y-0 left-0 w-[46%] rounded-full bg-gradient-to-r from-red-900 to-designer-primary" />
                  <span className="absolute top-1/2 -mt-1.5 left-[22%] w-3 h-3 rounded-full bg-amber-300 border-2 border-[#080808]" title="Note" />
                  <span className="absolute top-1/2 -mt-1.5 left-[61%] w-3 h-3 rotate-45 rounded-[2px] bg-white border-2 border-[#080808]" title="Bookmark" />
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2">
                  <span className="text-[10px] font-mono font-bold text-amber-300 bg-amber-300/10 border border-amber-300/25 rounded px-1.5 py-0.5">4:12</span>
                  <span className="min-w-0 flex-1 text-[11px] text-white/70 truncate">Hold Alt while dragging to subtract shapes</span>
                  <FaBookmark className="ml-auto w-3 h-3 text-white/40 shrink-0" />
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#111] p-2.5 hidden sm:block min-w-0 overflow-hidden">
                <p className="text-[9px] font-mono tracking-[0.22em] text-designer-primary uppercase truncate">Week 2 · Vector</p>
                <div className="mt-2 h-1 rounded-full bg-white/10"><span className="block h-full w-1/4 rounded-full bg-designer-primary" /></div>
                <ul className="mt-3 space-y-1.5">
                  {LESSONS.map((l) => (
                    <li
                      key={l.t}
                      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] ${l.d === "active" ? "bg-designer-primary/15 text-white ring-1 ring-designer-primary/40" : "text-white/60"}`}
                    >
                      <span className={`w-4 h-4 shrink-0 rounded-full flex items-center justify-center text-[7px] ${l.d === "done" ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50" : l.d === "active" ? "bg-designer-primary text-white" : "ring-1 ring-white/20"}`}>
                        {l.d === "done" ? <FaCheck /> : l.d === "active" ? <FaPlay /> : null}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{l.t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AcademyRecordedSection;
